import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { showCookiePreferences, useExternalMediaConsent } from "../cookieConsent";
import TurnstileWidget from "../components/TurnstileWidget";
import { submitContact } from "../utils/contact";
import { verifyTurnstileToken } from "../utils/turnstile";

type ContactFormValues = {
  name: string;
  email: string;
  phone: string;
  message: string;
  callbackRequested: boolean;
  turnstileToken: string;
};

export default function Kontakt() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const externalMediaAccepted = useExternalMediaConsent();
  const [captchaKey, setCaptchaKey] = useState(0);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";
  const requiresCaptcha = Boolean(siteKey);
  const contactEmail = "info@rss-energie.de";
  const googleProfileUrl =
    "https://www.google.com/search?sca_esv=94f317455afcf545&hl=de&authuser=0&kgmid=/g/11n3ppz3jb&q=RSS+UG&shndl=30&source=sh/x/loc/uni/m1/1&kgs=e53b4474b08a1b04&shem=shrtsdl&utm_source=shrtsdl,sh/x/loc/uni/m1/1";
  const mapEmbedUrl = "https://www.google.com/maps?q=Horkheimer%20Stra%C3%9Fe%2024%2C%2074081%20Heilbronn&output=embed";
  const copyEmail = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(contactEmail);
        toast.success(t("common.footer.copyEmailSuccess"));
        return;
      }
      const textarea = document.createElement("textarea");
      textarea.value = contactEmail;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (ok) {
        toast.success(t("common.footer.copyEmailSuccess"));
      } else {
        toast.error(t("common.footer.copyError"));
      }
    } catch {
      toast.error(t("common.footer.copyError"));
    }
  };
  const contactSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(1, t("contactPage.form.validation.name")),
        email: z
          .string()
          .min(1, t("contactPage.form.validation.email"))
          .email(t("contactPage.form.validation.emailInvalid")),
        phone: z.string().min(1, t("contactPage.form.validation.phone")),
        message: z
          .string()
          .min(1, t("contactPage.form.validation.message"))
          .min(20, t("contactPage.form.validation.messageMin")),
        callbackRequested: z.boolean().optional(),
        turnstileToken: requiresCaptcha
          ? z.string().min(1, t("common.captcha.validation"))
          : z.string().optional(),
      }),
    [t, requiresCaptcha]
  );
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
      callbackRequested: false,
      turnstileToken: "",
    },
  });

  const contactBullets = useMemo(() => {
    const value = t("home.contact.bullets", { returnObjects: true });
    return Array.isArray(value) ? (value as string[]) : [];
  }, [t]);
  const phoneValue = watch("phone");
  const isCallbackDisabled = !phoneValue?.trim();

  return (
    <Container sx={{ py: { xs: 4, md: 6 } }}>
      <Box className="hero-shell" sx={{ p: { xs: 4, md: 6 }, position: "relative", mb: { xs: 4, md: 6 } }}>
        <Box className="hero-glow" />
        <Stack spacing={2} sx={{ position: "relative", zIndex: 1, maxWidth: 680 }}>
          <Typography variant="h1">{t("contactPage.hero.title")}</Typography>
          <Typography variant="body1" sx={{ opacity: 0.85 }}>
            {t("contactPage.hero.subtitle")}
          </Typography>
        </Stack>
      </Box>

      <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, border: "1px solid var(--border)", background: "var(--surface)" }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography variant="h2">{t("home.contact.title")}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {t("home.contact.body")}
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              {contactBullets.map((bullet) => (
                <Stack direction="row" spacing={1} alignItems="center" key={bullet}>
                  <CheckCircleRoundedIcon color="success" fontSize="small" />
                  <Typography variant="body2">{bullet}</Typography>
                </Stack>
              ))}
            </Stack>
            <Box sx={{ mt: 4, borderRadius: 3, overflow: "hidden" }}>
              <img
                src="/assets/van_parking_lot_view_1772341027385.png"
                alt="RSS-Energie Dienstfahrzeug auf Parkplatz"
                style={{ width: "100%", height: "auto", display: "block", aspectRatio: "16/10", objectFit: "cover" }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid var(--border)", background: "var(--surface)" }}>
              <Stack
                spacing={2}
                component="form"
                onSubmit={handleSubmit(async (values) => {
                  if (requiresCaptcha) {
                    const ok = await verifyTurnstileToken(values.turnstileToken);
                    if (!ok) {
                      toast.error(t("common.captcha.error"));
                      setValue("turnstileToken", "");
                      setCaptchaKey((value) => value + 1);
                      return;
                    }
                  }
                  const message = [
                    `Name: ${values.name}`,
                    `E-Mail: ${values.email}`,
                    `Telefon: ${values.phone}`,
                    `Rueckruf gewuenscht: ${values.callbackRequested ? "Ja" : "Nein"}`,
                    "",
                    values.message,
                  ].join("\n");
                  toast.loading(t("contactPage.toast.sending"), { id: "contact" });
                  const result = await submitContact({
                    name: values.name,
                    email: values.email,
                    subject: "Kontaktformular",
                    message,
                  });
                  if (!result.ok) {
                    const errorKey = result.status === 429 ? "common.toast.rateLimited" : "common.toast.error";
                    toast.error(t(errorKey), { id: "contact" });
                    return;
                  }
                  toast.success(t("contactPage.toast.success"), { id: "contact" });
                  reset();
                  setCaptchaKey((value) => value + 1);
                  navigate("/nachricht-gesendet?type=kontakt");
                })}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {t("contactPage.formTitle")}
                </Typography>
                <TextField
                  label={t("contactPage.form.name")}
                  size="small"
                  fullWidth
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  {...register("name")}
                />
                <TextField
                  label={t("contactPage.form.email")}
                  size="small"
                  fullWidth
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  {...register("email")}
                />
                <TextField
                  label={t("contactPage.form.phone")}
                  size="small"
                  fullWidth
                  error={Boolean(errors.phone)}
                  helperText={errors.phone?.message}
                  {...register("phone")}
                />
                <TextField
                  label={t("contactPage.form.message")}
                  size="small"
                  fullWidth
                  multiline
                  minRows={3}
                  error={Boolean(errors.message)}
                  helperText={errors.message?.message}
                  {...register("message")}
                />
                <Tooltip
                  title={t("contactPage.form.callbackHint")}
                  disableHoverListener={!isCallbackDisabled}
                  disableFocusListener={!isCallbackDisabled}
                  disableTouchListener={!isCallbackDisabled}
                >
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          disabled={isCallbackDisabled}
                          {...register("callbackRequested")}
                        />
                      }
                      label={t("contactPage.form.callback")}
                    />
                  </span>
                </Tooltip>
                {requiresCaptcha ? (
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {t("common.captcha.label")}
                    </Typography>
                    <Controller
                      control={control}
                      name="turnstileToken"
                      render={({ field }) => (
                        <TurnstileWidget
                          siteKey={siteKey}
                          resetKey={captchaKey}
                          onToken={(token) => field.onChange(token)}
                        />
                      )}
                    />
                    <Typography
                      variant="caption"
                      color={errors.turnstileToken ? "error" : "text.secondary"}
                    >
                      {errors.turnstileToken?.message ?? t("common.captcha.helper")}
                    </Typography>
                  </Stack>
                ) : null}
                <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t("contactPage.form.submitLoading") : t("contactPage.form.submit")}
                </Button>
                <Box
                  component="button"
                  type="button"
                  onClick={copyEmail}
                  aria-label={t("common.footer.copyEmailAria")}
                  sx={{
                    border: "none",
                    background: "none",
                    p: 0,
                    textAlign: "left",
                    cursor: "pointer",
                    color: "text.secondary",
                    "&:hover": { color: "text.primary" },
                  }}
                >
                  <Typography variant="caption">{t("home.contact.emailLabel")}</Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: { xs: 3, md: 4 },
          border: "1px solid var(--border)",
          background: "var(--surface)",
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography variant="h2">{t("contactPage.google.title")}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {t("contactPage.google.body")}
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              href={googleProfileUrl}
              target="_blank"
              rel="noreferrer"
              sx={{ mt: 3, borderRadius: 99, px: 3 }}
            >
              {t("contactPage.google.button")}
            </Button>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                borderRadius: { xs: 3, md: 5 },
                overflow: "hidden",
                border: "1px solid var(--border)",
                minHeight: { xs: 300, md: 420 },
              }}
            >
              {externalMediaAccepted ? <Box
                component="iframe"
                title={t("contactPage.google.mapTitle")}
                src={mapEmbedUrl}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                sx={{
                  width: "100%",
                  height: { xs: 300, md: 420 },
                  border: 0,
                  display: "block",
                }}
              /> : (
                <Stack spacing={2} alignItems="center" justifyContent="center"
                  sx={{ minHeight: { xs: 300, md: 420 }, p: 3, textAlign: "center" }}>
                  <Typography color="text.secondary">{t("contactPage.google.consentNotice")}</Typography>
                  <Button variant="outlined" onClick={() => { void showCookiePreferences(); }}>
                    {t("common.nav.cookieSettings")}
                  </Button>
                </Stack>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}
